import { StatTile } from '@orbitalwatch/design';
import type { StatTileTone } from '@orbitalwatch/design';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface StatProps {
    /** Eyebrow label; also names the tile. */
    label: ReactNode;
    /** The figure. `undefined` (or `isLoading`) shows a skeleton bar instead. */
    value: ReactNode;
    /** Makes the whole tile a router link (Overview: "Users 128" → `/users`). */
    to?: string;
    /** 12 px text-3 line under the figure ("3 privileged", "last run 2 h ago"). */
    hint?: ReactNode;
    /** Tint only when the number means something (`danger` = failures, `warning` = expiring). Default `neutral`. */
    tone?: StatTileTone;
    /** Small text-2 unit after the figure. */
    unit?: ReactNode;
    /** Top-right slot (a state dot, a short note). */
    glyph?: ReactNode;
    /** `lg` = 32 px figure (default), `md` = compact 24 px. */
    size?: 'md' | 'lg';
    isLoading?: boolean;
    /** Extra content under the hint (a `ProgressBar`). */
    children?: ReactNode;
}

/** Library `StatTile`, optionally wrapped in a router link (`ds.css` "stat link"); a skeleton while loading. */
export function Stat(props: StatProps) {
    const loading = props.isLoading || props.value === undefined;
    const tile = (
        <StatTile
            label={props.label}
            value={loading ? <span className="ow-table-skeleton ark-stat-skeleton" aria-label="Loading" /> : props.value}
            unit={loading ? undefined : props.unit}
            glyph={props.glyph}
            tone={props.tone}
            size={props.size}
        >
            {props.hint ? <p className="ark-stat-hint">{props.hint}</p> : null}
            {props.children}
        </StatTile>
    );
    return props.to ? <Link to={props.to} className="ark-stat-link">{tile}</Link> : tile;
}
